import React, { useState, useEffect } from 'react';
import { Users, Share2, Check, Clock, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { normalizeUserData, getUserFields } from '../services/userService';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const MyNetwork = () => {
  const { user: authUser, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated || !authUser) {
      navigate('/signin');
      return;
    }

    const normalizedUser = normalizeUserData(authUser);
    const userFields = getUserFields(normalizedUser);
    setUser(userFields);
    
    // Set initial invite message with user's invite code
    const inviteCode = userFields.inviteCode || (authUser.invite_code || authUser.inviteCode) || 'UNKNOWN';
    setInviteMessage(
      `Join me on DonorsHouse and help save lives! Use my invite code ${inviteCode} to get started. Together we can make a difference in our community.`
    );

    fetchNetworkData(userFields.id || authUser.id);
  }, [authUser, isAuthenticated, authLoading, navigate]);

  const fetchNetworkData = async (userId) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Fetch connections
      const connectionsResponse = await axios.get(`${API}/connections/${userId}`);
      const connectionsData = connectionsResponse.data || [];
      
      // Normalize connections data
      const normalizedConnections = connectionsData.map(conn => ({
        id: conn.id,
        name: conn.name,
        bloodType: conn.blood_type || conn.bloodType,
        isBloodDonor: conn.is_blood_donor || conn.isBloodDonor || false,
        isOrganDonor: conn.is_organ_donor || conn.isOrganDonor || false,
        organsPledged: conn.organs_pledged || conn.organsPledged || [],
        joinedVia: conn.joined_via || conn.joinedVia,
        joinedDate: conn.created_at || conn.createdAt
      }));
      setConnections(normalizedConnections);

      // Fetch pending invitations
      const invitesResponse = await axios.get(`${API}/invitations/${userId}`);
      const invitesData = invitesResponse.data || [];
      
      const normalizedInvites = invitesData
        .filter(inv => inv.status === 'pending')
        .map(inv => ({
          id: inv.id,
          email: inv.invitee_email || inv.inviteeEmail,
          sentDate: inv.created_at || inv.createdAt,
          status: inv.status
        }));
      setPendingInvites(normalizedInvites);
    } catch (error) {
      // Error fetching network data - handled silently
      setConnections([]);
      setPendingInvites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInviteCode = () => {
    if (!user) return;
    const inviteCode = user.inviteCode || (authUser?.invite_code || authUser?.inviteCode) || '';
    navigator.clipboard.writeText(inviteCode);
    toast({ title: 'Copied!', description: 'Invite code copied to clipboard.' });
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!user || !authUser) {
      toast({ 
        title: 'Error', 
        description: 'User not authenticated', 
        variant: 'destructive' 
      });
      return;
    }

    const formData = new FormData(e.target);
    const inviteeEmail = formData.get('invite-email');
    const customMessage = formData.get('invite-message') || inviteMessage;

    if (!inviteeEmail) {
      toast({ 
        title: 'Error', 
        description: 'Email address is required', 
        variant: 'destructive' 
      });
      return;
    }

    try {
      const userId = user.id || authUser.id;
      const inviteCode = user.inviteCode || (authUser.invite_code || authUser.inviteCode);
      
      if (!inviteCode) {
        toast({ 
          title: 'Error', 
          description: 'Invite code not found', 
          variant: 'destructive' 
        });
        return;
      }

      await axios.post(`${API}/invitations`, {
        inviter_id: userId,
        invitee_email: inviteeEmail,
        invite_code: inviteCode,
        status: 'pending'
      });

      toast({ 
        title: 'Invite Sent', 
        description: 'Your invitation has been sent successfully.' 
      });
      setShowInviteDialog(false);
      
      // Refresh network data
      fetchNetworkData(userId);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send invitation. Please try again.';
      toast({ 
        title: 'Error', 
        description: errorMessage, 
        variant: 'destructive' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Network</h1>
            <p className="text-gray-700 text-lg">Grow your impact by inviting others to join</p>
          </div>
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-700 hover:bg-red-900 text-white btn-transition">
                <Share2 className="h-4 w-4 mr-2" />
                Invite Friends
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Invite Friends to DonorsHouse</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSendInvite} className="space-y-4">
                <div>
                  <Label htmlFor="invite-email" className="text-gray-900">Email Address</Label>
                  <Input id="invite-email" name="invite-email" type="email" placeholder="friend@example.com" required className="border-gray-300" />
                </div>
                <div>
                  <Label htmlFor="invite-message" className="text-gray-900">Custom Message (Optional)</Label>
                  <Textarea
                    id="invite-message"
                    name="invite-message"
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    rows={4}
                    className="border-gray-300"
                  />
                </div>
                <div className="p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-700">Your invite code: <span className="font-mono font-bold">{user?.inviteCode || (authUser?.invite_code || authUser?.inviteCode) || 'UNKNOWN'}</span></p>
                </div>
                <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                  Send Invitation
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Invite Code Card */}
        <Card className="shadow-md mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Your Invite Code</h3>
                <p className="text-3xl font-mono font-bold text-red-700">
                  {loading ? 'Loading...' : (user?.inviteCode || (authUser?.invite_code || authUser?.inviteCode) || 'UNKNOWN')}
                </p>
              </div>
              <Button
                onClick={handleCopyInviteCode}
                className="bg-red-700 hover:bg-red-900 text-white btn-transition"
                disabled={!user}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Code
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Network Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-red-700 mb-2">{connections.length}</div>
              <p className="text-gray-700 font-medium">Network Members</p>
            </CardContent>
          </Card>
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-red-700 mb-2">{pendingInvites.length}</div>
              <p className="text-gray-700 font-medium">Pending Invites</p>
            </CardContent>
          </Card>
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-red-700 mb-2">
                {connections.filter(c => c.isBloodDonor || c.isOrganDonor).length}
              </div>
              <p className="text-gray-700 font-medium">Active Donors</p>
            </CardContent>
          </Card>
        </div>

        {/* My Connections */}
        <Card className="shadow-md mb-8">
          <CardHeader>
            <CardTitle className="text-gray-900">My Connections</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading connections...</p>
              </div>
            ) : connections.length > 0 ? (
              <div className="space-y-4">
                {connections.map(connection => (
                  <div key={connection.id} className="p-4 border border-gray-200 rounded-lg card-transition hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="font-bold text-gray-900 text-lg mr-3">{connection.name}</h4>
                          <Badge className="bg-red-700 text-white">{connection.bloodType}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {connection.isBloodDonor && (
                            <Badge className="bg-green-700 text-white">Blood Donor</Badge>
                          )}
                          {connection.isOrganDonor && (
                            <Badge className="bg-blue-700 text-white">Organ Donor</Badge>
                          )}
                        </div>
                        {connection.organsPledged.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-700 font-semibold mb-1">Organs Pledged:</p>
                            <div className="flex flex-wrap gap-1">
                              {connection.organsPledged.map((organ, index) => (
                                <Badge key={index} variant="outline" className="text-xs text-gray-900 border-gray-900">
                                  {organ}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-600 mt-3">
                          Joined via your invite • {new Date(connection.joinedDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-700">No connections yet. Invite friends to grow your network!</p>
            )}
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-900">Pending Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading invitations...</p>
              </div>
            ) : pendingInvites.length > 0 ? (
              <div className="space-y-3">
                {pendingInvites.map(invite => (
                  <div key={invite.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-md">
                    <div>
                      <p className="font-medium text-gray-900">{invite.email}</p>
                      <p className="text-sm text-gray-600">Sent on {new Date(invite.sentDate).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="outline" className="text-orange-700 border-orange-700">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-700">No pending invitations.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyNetwork;